import { WixFormFields } from '../../../../types/wix-forms';
import { CreateActionParams } from '../actions';
import type { FormSubmission } from '@wix/ambassador-forms-v4-submission/types';
import { FormValues } from '@wix/form-viewer';
import { IntakeFormErrorType } from '../../../../types/errors';
import { CompletionRequirement } from '@wix/ambassador-bookings-services-v2-service/types';
import { IntakeFormStatus } from '../../../../utils/state/types';
import { splitFullName } from '../../../../utils/nameParser';

export type OpenIntakeFormLightbox = (
  formId: string,
  bookingFormValues?: FormValues,
) => Promise<void>;

export function createOpenIntakeFormLightboxAction({
  context,
  internalActions,
  getControllerState,
}: CreateActionParams): OpenIntakeFormLightbox {
  return async (formId: string, bookingFormValues?: FormValues) => {
    const { wixSdkAdapter } = context;
    const [state, setState] = getControllerState();
    const { intakeForms } = state;
    const currentIntakeForm = intakeForms?.[formId];

    const intakeFormLightbox = await wixSdkAdapter.getIntakeFormLightbox();

    if (!intakeFormLightbox) {
      internalActions.errorHandlers.addError(
        IntakeFormErrorType.INTAKE_FORM_LIGHTBOX_NOT_FOUND,
      );
      return;
    }

    const formValues = bookingFormValues;

    const { firstName, lastName } = splitFullName({
      firstName: formValues?.[WixFormFields.FIRST_NAME] as string,
      lastName: formValues?.[WixFormFields.LAST_NAME] as string,
    });

    const email = formValues?.[WixFormFields.EMAIL];

    if (!email) {
      internalActions.errorHandlers.addError(
        IntakeFormErrorType.COMPLETE_CLIENT_DETAILS_BEFORE_INTAKE,
      );
      return;
    }

    const initialValues = {
      [WixFormFields.FIRST_NAME]: firstName,
      [WixFormFields.LAST_NAME]: lastName,
      [WixFormFields.EMAIL]: email,
      [WixFormFields.PHONE]: formValues[WixFormFields.PHONE],
      [WixFormFields.ADDRESS]: formValues[WixFormFields.ADDRESS],
    };

    try {
      const lightboxData = {
        formId,
        initialValues,
        onSuccess: (submission: FormSubmission) => {
          setState({
            intakeForms: {
              ...intakeForms,
              [formId]: {
                ...currentIntakeForm!,
                status: IntakeFormStatus.COMPLETED,
                formData: submission,
              },
            },
          });
        },
        onClose: () => {
          if (
            currentIntakeForm?.completionRequirement ===
              CompletionRequirement.REQUIRED_BEFORE_BOOKING &&
            intakeForms?.[formId]?.status !== IntakeFormStatus.COMPLETED
          ) {
            internalActions.errorHandlers.addError(
              IntakeFormErrorType.REQUIRED_INTAKE_FORM_NOT_COMPLETED,
            );
          }
        },
      };

      await wixSdkAdapter.openLightbox(intakeFormLightbox.id!, lightboxData);
    } catch (error) {
      console.error('Failed to open intake form lightbox:', error);
    }
  };
}
